import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Camera, Upload, CheckCircle, XCircle, Image as ImageIcon, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PhotoUpload = () => {
  const [selectedWO, setSelectedWO] = useState(null);
  const [woSearch, setWoSearch] = useState('');
  const [woResults, setWoResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showRecents, setShowRecents] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const token = localStorage.getItem('token');

  // Recent WOs from localStorage (shared with Shop Tracker)
  const getRecentWOs = () => {
    try { return JSON.parse(localStorage.getItem('shop_recent_wos') || '[]'); } catch { return []; }
  };
  const saveRecentWO = (wo) => {
    const recents = getRecentWOs().filter(r => r.project_number !== wo.project_number);
    recents.unshift({ project_number: wo.project_number, client_name: wo.client_name || '', title: wo.title || wo.description || '' });
    localStorage.setItem('shop_recent_wos', JSON.stringify(recents.slice(0, 5)));
  };

  const searchWO = async (q) => {
    setWoSearch(q);
    setShowRecents(false);
    if (q.length < 2) { setWoResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/shop/work-orders`, { params: { q }, headers: { Authorization: `Bearer ${token}` } });
      setWoResults(r.data.slice(0, 8));
    } catch { setWoResults([]); }
    setSearching(false);
  };

  const handleWOFocus = () => {
    if (!woSearch && !selectedWO) {
      const recents = getRecentWOs();
      if (recents.length > 0) {
        setWoResults(recents);
        setShowRecents(true);
      }
    }
  };

  const selectWO = (wo) => {
    setSelectedWO(wo);
    setWoSearch(wo.project_number);
    setWoResults([]);
    setShowRecents(false);
    saveRecentWO(wo);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedWO) { toast.error('Select a work order first'); return; }

    const cameraFiles = Array.from(cameraInputRef.current?.files || []);
    const galleryFiles = Array.from(fileInputRef.current?.files || []);
    const files = cameraFiles.length > 0 ? cameraFiles : galleryFiles;
    if (files.length === 0) { toast.error('Select or capture a photo first'); return; }

    try {
      setUploading(true);
      let successCount = 0;

      for (const file of files) {
        const formData = new FormData();
        formData.append('work_order_number', selectedWO.project_number);
        formData.append('photo', file);

        const response = await axios.post(`${API}/field-employee/upload-photo`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });

        setUploadedPhotos(prev => [{
          filename: response.data.filename,
          work_order: response.data.work_order,
          folder: response.data.folder,
          timestamp: new Date().toLocaleString()
        }, ...prev]);
        successCount++;
      }

      toast.success(`${successCount} photo(s) uploaded!`);
      setPreviewImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Upload Work Order Photos</h1>

      {/* Work Order Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <label className="block text-sm font-medium text-slate-700 mb-2">Work Order</label>
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            data-testid="photo-wo-search"
            type="text"
            value={woSearch}
            onChange={e => searchWO(e.target.value)}
            onFocus={handleWOFocus}
            placeholder="Search by WO#, customer, or description..."
            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
          {searching && <RefreshCw className="absolute right-3 top-3 w-5 h-5 text-blue-700 animate-spin" />}
        </div>

        {woResults.length > 0 && (
          <div className="mt-2 max-h-60 overflow-y-auto bg-white border border-slate-300 rounded-lg divide-y divide-slate-200">
            {showRecents && (
              <div className="px-4 py-1.5 bg-white text-xs text-slate-500 font-medium uppercase tracking-wider">Recent Jobs</div>
            )}
            {woResults.map(wo => (
              <button
                key={wo.project_number}
                onClick={() => selectWO(wo)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <span className="font-mono text-blue-700 font-bold">{wo.project_number}</span>
                <span className="mx-2 text-slate-500">|</span>
                <span className="text-slate-700">{wo.client_name || 'No customer'}</span>
                {wo.title && <p className="text-xs text-slate-500 mt-0.5 truncate">{wo.title}</p>}
              </button>
            ))}
          </div>
        )}

        {selectedWO && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <div>
              <span className="font-mono text-blue-700 font-bold text-lg">{selectedWO.project_number}</span>
              <span className="mx-2 text-slate-500">|</span>
              <span className="text-slate-900">{selectedWO.client_name}</span>
              {selectedWO.title && <p className="text-sm text-slate-500 mt-0.5">{selectedWO.title}</p>}
            </div>
            <button onClick={() => { setSelectedWO(null); setWoSearch(''); }} className="text-slate-500 hover:text-slate-900">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Photo Capture/Upload */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <label className="block text-sm font-medium text-slate-700 mb-3">Capture or Upload Photo</label>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => cameraInputRef.current.click()}
            className="h-24 flex flex-col items-center justify-center gap-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 hover:text-slate-900 hover:border-blue-500 transition-colors"
          >
            <Camera className="h-8 w-8" />
            <span className="text-sm font-medium">Take Photo</span>
          </button>
          <button
            onClick={() => fileInputRef.current.click()}
            className="h-24 flex flex-col items-center justify-center gap-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 hover:text-slate-900 hover:border-blue-500 transition-colors"
          >
            <Upload className="h-8 w-8" />
            <span className="text-sm font-medium">From Gallery</span>
          </button>
        </div>

        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handleFileSelect} className="hidden" />
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />

        {previewImage && (
          <div className="mt-4 border border-slate-300 rounded-xl overflow-hidden">
            <img src={previewImage} alt="Preview" className="w-full h-64 object-contain bg-white" />
          </div>
        )}
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={uploading || !selectedWO || !previewImage}
        className="w-full py-4 rounded-xl font-semibold text-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-500 text-white transition-colors"
      >
        {uploading ? 'Uploading...' : 'Upload Photo'}
      </button>

      {/* Upload History */}
      {uploadedPhotos.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-700" /> Recently Uploaded
          </h3>
          <div className="space-y-2">
            {uploadedPhotos.map((photo, idx) => (
              <div key={idx} className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-emerald-700 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-900 text-sm font-medium truncate">{photo.filename}</p>
                    <p className="text-xs text-slate-500">WO: {photo.work_order} &bull; {photo.timestamp}</p>
                  </div>
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 text-center">
        Photos are saved to Google Drive in a folder named with your name and date.
      </p>
    </div>
  );
};

export default PhotoUpload;
